import { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Modal, Table, Tag } from 'antd';
import {
  checkRolePermission,
  formatNumber,
  hashColor,
  numberToMoneyStr,
} from '../../entities/util-functions';
import {
  ModalComponent,
  Title,
  CloseIcon,
  Datepicker,
  Button,
  Subtitle,
} from '../shared';
import FilesViewModal from '../shared/pictures-wall/FilesViewModal';
import GrowerDetailModal from '../farm-modals/GrowerDetailModal';
import HarvestCompleteModal from '../farm-modals/HarvestCompleteModal';
import GrowerHarvestModal from '../farm-modals/GrowerHarvestModal';
import WeatherInfoModal from '../farm-modals/WeatherInfoModal';
import weather_icon from '../../images/weather-icon.png';
import {
  IHarvestResource,
  IMusselHarvest,
  IMusselSeeding,
  IOysterHarvest,
  ISeaweedHarvest,
  ISeedingResource,
} from '../../entities/growing.entities';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import {
  SelectAccountSetting,
  SelectIsGrower,
  SelectProfileRole,
} from '../../store/extra/extra.selector';
import {
  ILineResource,
  IMusselLineResource,
} from '../../entities/farms.entities';
import {
  defaultDateFormat,
  directionChar,
  displayValueUnit,
  toMillisecond,
  toSecond,
} from '../../lib/common.helpers';
import DotsMenu, { IMenu } from '../shared/dropdown-menu/DotsMenu';
import { sendSingleRequest } from '../../apis';
import {
  IWeatherConditionResource,
  TBusinessType,
} from '../../entities/general.entities';
import { lineLengthLabel } from '../../lib/farm.helpers';
import { ACTIVITY_COLORS } from '../../lib/constant';
import { selectBoats } from '../../store/users/users.selector';
import { selectIsAdmin } from '../../store/auth/auth.selector';
import { IBoatResource } from '../../entities/boat.entities';

const displayHarvestTime = (st: number, en: number) => {
  st = st * 1000;
  en = en * 1000;
  return moment(st).format('YYYY-MM-DD') === moment(en).format('YYYY-MM-DD')
    ? `${moment(st).format('LT')} - ${moment(en).format('LT')}`
    : `${moment(st).format('MMM Do')} ${moment(st).format('LT')} - ${moment(
        en,
      ).format('MMM Do')} ${moment(en).format('LT')}`;
};

interface IColumn {
  key: string;
  title: string;
  dataIndex?: string;
  render: any;
}

const basicColumns = [
  {
    key: 'season_name',
    title: 'Season',
    render: (x: IHarvestResource) => <span>{x.season_name}</span>,
  },
  {
    key: 'taxonomy_groups',
    title: 'Taxonomy',
    render: (x: IHarvestResource) => (
      <div className='d-flex flex-col gap-4'>
        {x.taxonomy_groups?.map(t => (
          <Tag key={t.id} color={hashColor(t.name)}>
            {t.name}
          </Tag>
        ))}
      </div>
    ),
  },
  {
    key: 'complete_date',
    title: 'Harvested date',
    render: (x: IHarvestResource) => (
      <>
        <div>{defaultDateFormat(x.complete_date)}</div>
        {!!x.harvester_name && (
          <div style={{ whiteSpace: 'nowrap' }}>{x.harvester_name}</div>
        )}
      </>
    ),
  },
];

const oysterColumns = (boats: IBoatResource[]) => [
  ...basicColumns,
  {
    key: 'vessel',
    title: 'Vessel',
    render: (x: IOysterHarvest) => {
      const boat = boats.find(b => b.id === x.boat_id);
      return boat ? (
        <span style={{ color: hashColor(boat.reg_number) }}>{boat.name}</span>
      ) : (
        <span>-</span>
      );
    },
  },
  {
    key: 'amount',
    title: 'Amount',
    render: (x: IOysterHarvest) =>
      x.line_length === null || x.line_length === undefined ? (
        <>
          {x.measurement_units === 'pcs' ? (
            <span>{`${formatNumber(x.amount_pcs)} pcs`}</span>
          ) : (
            <span>{`${formatNumber(x.amount_dz)} dz`}</span>
          )}
        </>
      ) : (
        <span>
          {x.measurement_units === 'pcs'
            ? `${formatNumber(x.amount_pcs)} pcs / ${x.line_length} ${
                x.line.farming_method
              }`
            : `${formatNumber(x.amount_dz)} dz / ${x.line_length} ${
                x.line.farming_method
              }`}
        </span>
      ),
  },
  {
    key: 'income',
    title: 'Income',
    render: (x: IOysterHarvest) => (
      <span>{`$${numberToMoneyStr(x.income ?? 0)}`}</span>
    ),
  },
  {
    key: 'is_full',
    title: 'F/P',
    render: (x: IOysterHarvest) => (
      <span>{x.is_full ? 'Full' : 'Partial'}</span>
    ),
  },
  {
    key: 'is_final',
    title: 'Final',
    render: (x: IOysterHarvest) => (
      <span>{x.is_waste ? '-' : x.is_final ? 'Final' : 'Middle'}</span>
    ),
  },
];

const seaweedColumns = (ut: any) => [
  ...basicColumns,
  {
    key: 'tank_area',
    title: 'Tank area',
    render: (x: ISeaweedHarvest) => (
      <span>{displayValueUnit(x.tank_area, ut, 'area_sqm')}</span>
    ),
  },
  {
    key: 'amount',
    title: 'Amount',
    render: (x: ISeaweedHarvest) => (
      <span>{displayValueUnit(x.amount, ut, 'weight_kg')}</span>
    ),
  },
  {
    key: 'income',
    title: 'Income',
    render: (x: ISeaweedHarvest) => (
      <span>{`$${numberToMoneyStr(x.income ?? 0)}`}</span>
    ),
  },
  {
    key: 'is_full',
    title: 'F/P',
    render: (x: ISeaweedHarvest) => (
      <span>{x.is_full ? 'Full' : 'Partial'}</span>
    ),
  },
  {
    key: 'is_final',
    title: 'Final',
    render: (x: ISeaweedHarvest) => (
      <span>{x.is_waste ? '-' : x.is_final ? 'Final' : 'Middle'}</span>
    ),
  },
];

const musselColumns = (
  type: IMusselLineResource['line_type'],
  seedings: IMusselSeeding[],
  farmingMethod: string | null | undefined,
  unitType: any,
  lang: any,
  boats: IBoatResource[],
) => [
  ...basicColumns,
  {
    key: 'line_length',
    title: type === 'LONGLINE' ? 'Harvested length' : 'Number of droppers',
    render: (x: IMusselHarvest) => {
      const seededLen = seedings
        .filter(
          y =>
            x.harvest_groups &&
            x.harvest_groups?.some(t => t.batch_seed_id === y.id),
        )
        .reduce((p, c) => p + c.line_length, 0);
      return seededLen > 0 ? (
        <span>
          <span style={{ color: ACTIVITY_COLORS.seeding }}>
            {translate(
              lang,
              '%s seeded',
              lineLengthLabel(
                'MUSSEL',
                type,
                farmingMethod,
                unitType,
                seededLen,
              ),
            )}
          </span>
          <br />
          <span style={{ color: ACTIVITY_COLORS.harvest }}>
            {translate(
              lang,
              '%s harvested',
              displayValueUnit(
                x.line_length,
                unitType,
                type === 'LONGLINE' ? 'distance_m' : 'droppers',
                '-',
              ),
            )}
          </span>
        </span>
      ) : (
        <span>
          {displayValueUnit(
            x.line_length,
            unitType,
            type === 'LONGLINE' ? 'distance_m' : 'droppers',
            '-',
          )}
        </span>
      );
    },
    width: 145,
  },
  {
    key: 'harvest_number',
    title: 'Harvest number',
    render: (x: IMusselHarvest) => <span>{x.harvest_number}</span>,
  },
  {
    key: 'vessel',
    title: 'Vessel',
    render: (x: IMusselHarvest) => {
      const boat = boats.find(b => b.id === x.boat_id);
      return boat ? (
        <span style={{ color: hashColor(boat.reg_number) }}>{boat.name}</span>
      ) : (
        <span>-</span>
      );
    },
  },
  {
    key: 'time_tracks',
    title: 'Time Track',
    render: (x: IMusselHarvest) =>
      x.time_tracks &&
      x.time_tracks.map((t: any, i: number) => (
        <div key={i}>{displayHarvestTime(t.start_time, t.finish_time)}</div>
      )),
  },
  {
    key: 'amount',
    title: 'Amount',
    render: (x: IMusselHarvest) => (
      <span>
        {x.is_waste
          ? 'Stripped to waste'
          : displayValueUnit(x.amount, unitType, 'weight_kg')}
      </span>
    ),
  },
  {
    key: 'income',
    title: 'Income',
    render: (x: IMusselHarvest) => (
      <span>{x.is_waste ? '' : `$${numberToMoneyStr(x.income ?? 0)}`}</span>
    ),
  },
  {
    key: 'is_full',
    title: 'F/P',
    render: (x: IMusselHarvest) => (
      <span>{x.is_full ? 'Full' : 'Partial'}</span>
    ),
  },
  {
    key: 'is_final',
    title: 'Final',
    render: (x: IMusselHarvest) => (
      <span>{x.is_waste ? '-' : x.is_final ? 'Final' : 'Middle'}</span>
    ),
  },
];

interface DateChangeProps {
  visible: boolean;
  onClose: () => void;
  onUpdate?: (d: any) => void;
  harvestData: IHarvestResource;
}

const DateChangeModal = ({
  visible,
  onClose,
  onUpdate,
  harvestData,
}: DateChangeProps) => {
  const lang = useSelector(selectLang);

  const [date, setDate] = useState(
    harvestData ? toMillisecond(harvestData.complete_date) : Date.now(),
  );
  const [disabled, setDisabled] = useState(false);

  const confirmClick = async () => {
    setDisabled(true);
    const response = harvestData
      ? await sendSingleRequest(
          {
            complete_date: toSecond(date),
          },
          'POST',
          `api/farm/line/harvest-update/${harvestData.id}`,
          true,
        )
      : await sendSingleRequest({}, 'POST', '');
    if (response.status) {
      if (onUpdate) {
        await onUpdate(response.data);
      }
      onClose();
    } else {
      window.alert(translate(lang, response.data?.message || 'Unknown error'));
      setDisabled(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      className='harvest-date-modal'
      closable
      closeIcon={<CloseIcon />}
      width={500}
      footer={null}
    >
      <div className='section-modal wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, 'Change harvest date')}
          </Subtitle>
        </div>
        <div className='mb-32'>
          <Datepicker
            defaultValue={date}
            label={translate(lang, '_harvest_complete_date')}
            onChange={e => e && setDate(e.toDate().getTime())}
            disabled={disabled}
          />
        </div>
        <div className='modal-button d-flex justify-end'>
          <Button
            width={'small'}
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={confirmClick}
            disabled={disabled}
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

interface Props {
  cropType: TBusinessType;
  lineData: ILineResource;
  harvests: IHarvestResource[];
  seedings: ISeedingResource[];
  onUpdateRow?: (d: any) => void;
  farmID: number;
}

const HarvestsTable = ({
  cropType,
  lineData,
  harvests,
  seedings,
  onUpdateRow,
  farmID,
}: Props) => {
  const history = useHistory();
  const lang = useSelector(selectLang);
  const isGrower = SelectIsGrower(farmID);
  const accSetting = SelectAccountSetting(farmID);
  const myRole = SelectProfileRole(farmID);
  const isAdmin = useSelector(selectIsAdmin);
  const boats = useSelector(selectBoats);

  const [files, setFiles] = useState<string[]>();
  const [infoText, setInfoText] = useState<string>();
  const [openHarvest, setOpenHarvest] = useState<IHarvestResource>();
  const [editHarvest, setEditHarvest] = useState<IHarvestResource>();
  const [deleteHarvest, setDeleteHarvest] = useState<IHarvestResource>();
  const [editDate, setEditDate] = useState<IHarvestResource>();
  const [disabled, setDisabled] = useState(false);
  const [viewMoreHarvest, setViewMoreHarvest] = useState<IHarvestResource>();
  const [viewWeather, setViewWeather] = useState<IWeatherConditionResource>();

  const openFiles = (fileUrls: string[]) => setFiles(fileUrls);

  const openInfo = (comment: any) => {
    setInfoText(comment && comment.length > 0 ? comment : 'No comments yet');
  };
  const confirmDelete = async () => {
    if (!deleteHarvest) return;

    setDisabled(true);
    const res = await sendSingleRequest(
      {},
      'DELETE',
      `api/farm/line/harvest/${deleteHarvest.id}`,
      true,
    );
    if (res.status) {
      onUpdateRow && onUpdateRow(deleteHarvest.id);
      setDeleteHarvest(undefined);
      setDisabled(false);
    } else {
      setDisabled(false);
      window.alert(res.data?.message ?? 'Server error!');
    }
  };

  const viewMoreRows = useMemo(() => {
    if (!viewMoreHarvest) return [];
    const h = viewMoreHarvest;
    const mh = h as IMusselHarvest;
    const oh = h as IOysterHarvest;
    const rows: { label: string; value: string | number | null }[] = [
      { label: 'Direction', value: directionChar(h.direction, '-') },
      {
        label: 'Shell length',
        value:
          h.shell_length != null
            ? h.shell_length_max != null
              ? `${h.shell_length} - ${h.shell_length_max}`
              : String(h.shell_length)
            : null,
      },
      { label: 'Bags quantity', value: h.bags_quantity },
      { label: 'Signature', value: h.signature },
    ];
    if (cropType === 'MUSSEL') {
      rows.push(
        { label: 'Shell condition', value: mh.shell_condition },
        { label: 'Company', value: mh.company },
        { label: 'Tag color', value: mh.tag_color },
        { label: 'Port of unload', value: mh.port_of_unload },
        { label: 'Crop owner', value: mh.crop_owner },
        { label: 'Growing area', value: mh.growing_area },
        { label: 'Delivered to', value: mh.delivered_to },
        { label: 'Packhouse', value: mh.packhouse },
        { label: 'Mussel type', value: mh.mussel_type },
        { label: 'Mussels', value: mh.mussels },
        { label: 'Meat yield', value: mh.meat_yield },
        { label: 'Blues', value: mh.blues },
        { label: 'Marine waste', value: mh.marine_waste },
        { label: 'Backbone OK', value: mh.backbone_ok ? 'Yes' : 'No' },
        {
          label: 'Backbone replace',
          value: mh.backbone_replace ? 'Yes' : 'No',
        },
        {
          label: 'Lights/IDs in place',
          value: mh.lights_ids_in_place ? 'Yes' : 'No',
        },
        {
          label: 'Flotation on farm',
          value: mh.flotation_on_farm ? 'Yes' : 'No',
        },
        { label: 'Rope bags quantity', value: mh.rope_bags_quantity },
        { label: 'Product left on line', value: mh.product_left_on_line },
        { label: 'Profit per meter', value: mh.profit_per_meter },
      );
    }
    if (cropType === 'OYSTER') {
      rows.push(
        {
          label: 'Return amount',
          value:
            oh.return_amount_pcs != null || oh.return_amount_dz != null
              ? oh.measurement_units === 'pcs'
                ? `${formatNumber(oh.return_amount_pcs ?? 0)} pcs`
                : `${formatNumber(oh.return_amount_dz ?? 0)} dz`
              : null,
        },
        { label: 'Mortality', value: oh.mortality ?? null },
        {
          label: 'Shell condition',
          value: (oh.shell_condition ?? null) as string | null,
        },
      );
    }
    return rows;
  }, [viewMoreHarvest, cropType]);

  const tableColumns = () => {
    let result: IColumn[] =
      cropType === 'MUSSEL'
        ? [
            ...musselColumns(
              (lineData as IMusselLineResource).line_type,
              seedings as IMusselSeeding[],
              lineData.farming_method,
              accSetting?.display_unit,
              lang,
              boats,
            ),
          ]
        : cropType === 'OYSTER'
          ? [...oysterColumns(boats)]
          : [...seaweedColumns(accSetting?.display_unit)];

    result = [
      {
        key: 'id',
        title: 'ID',
        render: (x: IHarvestResource) =>
          x.is_sold ? (
            <span>
              <div>{x.id}</div>
              <div className='color-red'>{translate(lang, 'Sold')}</div>
            </span>
          ) : (
            <span>{x.id}</span>
          ),
      },
      ...result,
      {
        key: 'stored_batch',
        title: '_generated_spat_storage',
        render: (x: IHarvestResource) => (
          <span>{x.stored_batch?.source ?? '-'}</span>
        ),
      },
      {
        key: 'comment',
        dataIndex: 'comment',
        title: 'Comment',
        render: (x: any) => (
          <div
            className='btn__modal'
            onKeyDown={() => undefined}
            onClick={e => {
              e.stopPropagation();
              openInfo(x);
            }}
          >
            {translate(lang, 'View')}
          </div>
        ),
      },
      {
        key: 'images',
        title: 'Photo',
        render: (x: IHarvestResource) =>
          !!x && x.image_pdf_files && x.image_pdf_files?.length > 0 ? (
            <div
              className='btn__modal'
              onClick={e => {
                e.stopPropagation();
                openFiles(x.image_pdf_files ?? []);
              }}
            >
              {translate(lang, 'View')}
            </div>
          ) : (
            <span></span>
          ),
      },
    ];
    result.push({
      key: 'action',
      title: '',
      render: (x: IHarvestResource) => {
        const items: IMenu[] = [
          {
            label: translate(lang, 'View More Data'),
            onClick: () => setViewMoreHarvest(x),
          },
        ];
        if (
          !!onUpdateRow &&
          checkRolePermission({ allowedRoles: ['owner', 'admin'] }, myRole)
        ) {
          items.push(
            {
              label: translate(lang, 'Edit'),
              onClick: () => setEditHarvest(x),
            },
            {
              label: translate(lang, 'Delete'),
              onClick: () => setDeleteHarvest(x),
              disabled: !isAdmin,
            },
            {
              label: translate(lang, 'Change harvest date'),
              onClick: () => setEditDate(x),
            },
          );
        }
        return <DotsMenu items={items} />;
      },
    });
    if (!accSetting?.show_budget_menu) {
      result = result.filter(x => x.key !== 'income');
    }
    return result.map(x => ({ ...x, title: translate(lang, x.title) }));
  };

  return (
    <div className='ant-table-wrapper table mt-17'>
      <Title
        className='ml-7'
        size={6}
        color='black-3'
        align='default'
        fontWeight={700}
      >
        {translate(lang, 'Harvests')}
      </Title>
      <Table
        rowKey='id'
        className='table table--isFarm pl-0 pr-0 overflow-auto'
        pagination={false}
        columns={tableColumns()}
        dataSource={[...harvests].sort(
          (a, b) => a.complete_date - b.complete_date,
        )}
        onRow={dataRow => ({
          onClick: () => history.push(`/harvest/${dataRow.id}`),
        })}
      />
      {infoText !== undefined && (
        <ModalComponent
          visible={true}
          onCancel={() => setInfoText(undefined)}
          type=''
          title='Comment'
          text={translate(lang, infoText)}
        />
      )}
      {!!files && files.length > 0 && (
        <FilesViewModal
          visible={true}
          onClose={() => setFiles(undefined)}
          files={files}
        />
      )}
      {!!openHarvest && (
        <GrowerDetailModal
          visible={true}
          items={openHarvest.expense_items ?? []}
          onClose={() => setOpenHarvest(undefined)}
        />
      )}
      {!!editHarvest && !!onUpdateRow && (
        <>
          {isGrower ? (
            <GrowerHarvestModal
              type={cropType}
              visible={true}
              lineData={lineData ?? (editHarvest.line as any)}
              onConfirm={d => {
                setEditHarvest(undefined);
                onUpdateRow(d);
              }}
              onClose={() => setEditHarvest(undefined)}
              harvestData={editHarvest as IMusselHarvest}
              updateId={editHarvest.id}
            />
          ) : (
            <HarvestCompleteModal
              visible={true}
              title='Update harvest data'
              lineData={lineData ?? (editHarvest.line as any)}
              data={editHarvest}
              onCancel={() => setEditHarvest(undefined)}
              onConfirm={d => {
                setEditHarvest(undefined);
                onUpdateRow(d);
              }}
              updateID={editHarvest.id}
            />
          )}
        </>
      )}
      {deleteHarvest && (
        <ModalComponent
          title={translate(lang, 'Delete')}
          text={translate(lang, '_delete_data')}
          type='delete'
          visible={!!deleteHarvest}
          disabled={disabled}
          onCancel={() => setDeleteHarvest(undefined)}
          onConfirm={confirmDelete}
        />
      )}
      {!!editDate && (
        <DateChangeModal
          visible={true}
          onClose={() => setEditDate(undefined)}
          harvestData={editDate}
          onUpdate={onUpdateRow}
        />
      )}
      {!!viewMoreHarvest && (
        <Modal
          title={translate(lang, 'More Data')}
          visible={true}
          onCancel={() => setViewMoreHarvest(undefined)}
          footer={null}
          width={500}
          closable
        >
          <div className='wrap'>
            {viewMoreRows.map(row => (
              <div key={row.label} className='mb-17'>
                <Subtitle
                  size={3}
                  color='black-2'
                  align='left'
                  fontWeight={400}
                >
                  {translate(lang, row.label)}
                </Subtitle>
                <Subtitle
                  size={5}
                  color='black-5'
                  align='left'
                  fontWeight={400}
                >
                  {row.value ?? '-'}
                </Subtitle>
              </div>
            ))}
            {viewMoreHarvest.weather_condition && (
              <div className='mb-17'>
                <Subtitle
                  size={3}
                  color='black-2'
                  align='left'
                  fontWeight={400}
                >
                  {translate(lang, 'Weather')}
                </Subtitle>
                <div
                  className='btn__modal'
                  style={{ display: 'flex', alignItems: 'center' }}
                  onClick={() =>
                    setViewWeather(
                      viewMoreHarvest.weather_condition ?? undefined,
                    )
                  }
                >
                  <img
                    className='ant-image-img'
                    src={weather_icon}
                    alt='weather'
                    style={{ maxWidth: '20px' }}
                  />
                  <span style={{ marginLeft: '5px' }}>
                    {translate(lang, 'View')}
                  </span>
                </div>
              </div>
            )}
            {cropType === 'MUSSEL' &&
              !!accSetting?.track_original_spat &&
              !!(viewMoreHarvest as IMusselHarvest).harvest_spats?.length && (
                <div className='mb-17'>
                  <Subtitle
                    size={3}
                    color='black-2'
                    align='left'
                    fontWeight={400}
                  >
                    {translate(lang, 'Original Spats')}
                  </Subtitle>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      marginTop: '6px',
                      fontSize: '13px',
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 8px',
                            borderBottom: '1px solid #e8e8e8',
                          }}
                        >
                          {translate(lang, 'Source name')}
                        </th>
                        <th
                          style={{
                            textAlign: 'right',
                            padding: '4px 8px',
                            borderBottom: '1px solid #e8e8e8',
                          }}
                        >
                          {translate(lang, 'Harvest amount')} (kg)
                        </th>
                        <th
                          style={{
                            textAlign: 'right',
                            padding: '4px 8px',
                            borderBottom: '1px solid #e8e8e8',
                          }}
                        >
                          {translate(lang, 'Length')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewMoreHarvest as IMusselHarvest).harvest_spats!.map(
                        s => (
                          <tr key={s.id}>
                            <td style={{ padding: '4px 8px' }}>
                              {s.spat_storage.source}
                            </td>
                            <td
                              style={{ textAlign: 'right', padding: '4px 8px' }}
                            >
                              {formatNumber(s.amount)}
                            </td>
                            <td
                              style={{ textAlign: 'right', padding: '4px 8px' }}
                            >
                              {formatNumber(s.line_length)}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </Modal>
      )}
      {!!viewWeather && (
        <WeatherInfoModal
          data={viewWeather}
          visible={true}
          title='Weather Condition'
          onClose={() => setViewWeather(undefined)}
        />
      )}
    </div>
  );
};

export default HarvestsTable;
